/**
 * Payment Slice
 *
 * Handles both single and bulk payment fetches via POST /payments/fetch.
 * - fetchPayment: fetches one payment (for PaymentDetailPage)
 * - fetchBulkPayments: fetches up to 100 payments (for BulkPaymentsPage)
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentInitialState, PaymentResponse, FetchPaymentsResponse } from './paymentState';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

/** Fetches a single payment by ID. Stores result in state.data. */
export const fetchPayment = createAsyncThunk<
  PaymentResponse, string, { state: RootState; rejectValue: string }
>('payment/fetchSingle', async (paymentId, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const result = await beaconApi.fetchPayments([paymentId], apiBase);
    if (result.payments.length === 0) {
      return rejectWithValue(`Payment not found: ${paymentId}`);
    }
    return result.payments[0];
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch payment');
  }
});

/** Fetches multiple payments by IDs. Stores result in state.bulkData. */
export const fetchBulkPayments = createAsyncThunk<
  FetchPaymentsResponse, string[], { state: RootState; rejectValue: string }
>('payment/fetchBulk', async (paymentIds, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    return await beaconApi.fetchPayments(paymentIds, apiBase);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch payments');
  }
});

const paymentSlice = createSlice({
  name: 'payment',
  initialState: paymentInitialState,
  reducers: {
    clearPayment: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
    },
    clearBulkPayments: (state) => {
      state.bulkData = null;
      state.bulkLoading = false;
      state.bulkError = null;
      state.bulkRequestId = null;
    },
  },
  extraReducers: (builder) => {
    // Single payment
    builder
      .addCase(fetchPayment.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchPayment.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchPayment.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch payment';
      });

    // Bulk payments
    builder
      .addCase(fetchBulkPayments.pending, (state, action) => {
        state.bulkLoading = true;
        state.bulkError = null;
        state.bulkRequestId = action.meta.requestId;
      })
      .addCase(fetchBulkPayments.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.bulkRequestId) return;
        state.bulkLoading = false;
        state.bulkData = action.payload;
        state.bulkError = null;
      })
      .addCase(fetchBulkPayments.rejected, (state, action) => {
        if (action.meta.requestId !== state.bulkRequestId) return;
        state.bulkLoading = false;
        state.bulkError = action.payload || action.error.message || 'Failed to fetch payments';
      });
  },
});

export const { clearPayment, clearBulkPayments } = paymentSlice.actions;
export default paymentSlice.reducer;
